import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import {
  Menu,
  X,
  Home,
  Image,
  MapPin,
  MessageSquare,
  Building2,
} from "lucide-react";

import { siteData } from "../data/siteData";
import logo1 from "../assets/logo1.png";

const iconMap = {
  home: Home,
  project: Building2,
  gallery: Image,
  location: MapPin,
  contact: MessageSquare,
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  // Handle scroll for navbar background + active section
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      siteData.navigation.forEach((item) => {
        const section = document.getElementById(item.id);
        if (!section) return;

        const rect = section.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom >= 100) {
          setActiveSection(item.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed w-full z-50  bg-[#16243E] transition-all duration-300 ${
        isScrolled ? "shadow-lg" : "shadow-none"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <Link
              to="/"
              onClick={() => {
                if (window.location.pathname === "/") {
                  window.dispatchEvent(new Event("force-scroll-top"));
                }
              }}
              className="flex items-center space-x-3"
            >
              <img
                src={logo1}
                alt={`${siteData.company.name} Logo`}
                className="w-24 h-18 border-2 p-0 rounded-lg border-gray-400 object-contain"
              />
              <div className="sm:block hidden pb-2">
                <h1 className="text-2xl font-bold text-[#C2DF93]">
                  {siteData.company.name}
                </h1>
                <p className="text-xs text-[#C2DF93]">
                  by {siteData.company.parentCompany}
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {siteData.navigation.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => {
                  if (window.location.pathname === item.path) {
                    window.dispatchEvent(new Event("force-scroll-top"));
                  }
                }}
                className={`text-sm font-medium transition-colors ${
                  activeSection === item.id
                    ? "text-[#C2DF93]"
                    : "text-white hover:text-[#C2DF93]"
                }`}
              >
                {item.label}
              </Link>
            ))}

            <a
              href="/floret_brochure.pdf"
              download
              className="bg-transparent text-[#C2DF93] px-6 py-2.5 rounded-full border-2 border-[#C2DF93] hover:bg-[#C2DF93] hover:text-black font-medium hover:shadow-lg transition-all inline-flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Brochure</span>
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-gray-100"
            aria-label="Toggle Menu"
          >
            {!isMenuOpen && <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Navigation */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 lg:hidden z-50
    ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Close Button */}
        <button
          onClick={() => setIsMenuOpen(false)}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100"
          aria-label="Close Menu"
        >
          <X size={24} className="text-gray-700" />
        </button>

        <div className="px-6 py-6 flex flex-col h-full justify-between">
          <div className="space-y-4">
            {siteData.navigation.map((item) => {
              const Icon = iconMap[item.id];
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 w-full py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {Icon && <Icon size={20} className="text-emerald-600" />}
                  <span className="font-medium text-gray-700">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>

          <a
            href="https://drive.google.com/uc?export=download&id=1nKksAaIU4blOCQ2pMD0a6ZTTdIirtzAm
"
            download
            className="bg-transparent text-[#C2DF93] px-6 py-2.5 rounded-full border-2 border-[#C2DF93] hover:bg-[#C2DF93] hover:text-black font-medium hover:shadow-lg transition-all inline-flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download Brochure</span>
          </a>
        </div>
      </div>

      {/* Overlay to close sidebar */}
      {isMenuOpen && (
        <div
          onClick={() => setIsMenuOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-25 lg:hidden z-40"
        />
      )}
    </nav>
  );
};

export default Navbar;
